import { RefObject, useEffect } from 'react';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Keeps keyboard focus inside a modal while it is mounted: focus moves into it
 * on mount, Tab and Shift+Tab wrap at its edges, and focus returns to whatever
 * had it before once the modal unmounts.
 */
export default function useFocusTrap(containerRef: RefObject<HTMLElement | null>) {
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return undefined;

        const previouslyFocused = document.activeElement as HTMLElement | null;
        const focusable = () => Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
        (focusable()[0] ?? container).focus();

        const wrapTab = (event: KeyboardEvent) => {
            if (event.key !== 'Tab') return;
            const elements = focusable();
            if (elements.length === 0) {
                event.preventDefault();
                return;
            }
            const first = elements[0];
            const last = elements[elements.length - 1];
            const active = document.activeElement;
            const outside = !container.contains(active);
            if (event.shiftKey && (active === first || outside)) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && (active === last || outside)) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', wrapTab);
        return () => {
            document.removeEventListener('keydown', wrapTab);
            previouslyFocused?.focus();
        };
    }, [containerRef]);
}
